import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import type { ProfileRecord, ProfileUpdateInput, SupportedLocale } from "@/types/auth";

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new Error("Supabase environment variables are not configured.");
  }
}

export async function getProfileByUserId(userId: string): Promise<ProfileRecord | null> {
  ensureConfigured();

  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data as ProfileRecord | null;
}

export async function getCurrentProfile() {
  ensureConfigured();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return null;
  }

  return getProfileByUserId(user.id);
}

export async function updateCurrentProfile(
  input: ProfileUpdateInput,
): Promise<ProfileRecord> {
  ensureConfigured();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error("No authenticated user available.");
  }

  const currentProfile = await getProfileByUserId(user.id);
  const profilePayload = {
    first_name: input.first_name ?? currentProfile?.first_name ?? "Writer",
    last_name: input.last_name ?? currentProfile?.last_name ?? "Account",
    phone: input.phone === undefined ? (currentProfile?.phone ?? null) : (input.phone ?? null),
    language_pref: input.language_pref ?? currentProfile?.language_pref ?? "en",
  };

  const updateResult = await supabase
    .from("profiles")
    .update(profilePayload)
    .eq("id", user.id)
    .select("*")
    .maybeSingle();

  if (updateResult.error) {
    throw updateResult.error;
  }

  if (updateResult.data) {
    return updateResult.data as ProfileRecord;
  }

  const insertResult = await supabase
    .from("profiles")
    .insert({
      id: user.id,
      ...profilePayload,
    })
    .select("*")
    .single();

  if (insertResult.error) {
    throw insertResult.error;
  }

  return insertResult.data as ProfileRecord;
}

export async function updateLanguagePreference(language: SupportedLocale) {
  return updateCurrentProfile({
    language_pref: language,
  });
}

export async function requestCurrentAccountDeletion(password: string) {
  ensureConfigured();

  const { data, error } = await supabase.functions.invoke("account-deletion", {
    body: {
      action: "request",
      password,
    },
  });

  if (error) {
    throw error;
  }

  return data as {
    deletionRequestedAt: string;
    deletionScheduledAt: string;
    status: "pending_deletion";
  };
}
